import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { APPRECIATION_NOTE } from '../utils/cycle.helpers';

export default function BulletinElementaire() {
  const { studentId } = useParams();
  const [searchParams] = useSearchParams();
  const periodId = searchParams.get('periodId');
  const navigate = useNavigate();

  const [student, setStudent]   = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [school, setSchool]     = useState(null);
  const [period, setPeriod]     = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [classStats, setClassStats] = useState({ moyenneClasse: null, effectif: null, rang: null });
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  useEffect(() => {
    async function load() {
      try {
        // 1. Charger l'élève
        const stu = await api.get(`/students/${studentId}`);
        setStudent(stu);
        const enrollment = (stu.enrollments || [])[0];
        const clsId = enrollment?.classId;
        setClassInfo(enrollment?.class || { name: '—' });

        // 2. Charger l'établissement et la période
        const [schoolData, periods] = await Promise.all([
          api.get('/establishment').catch(() => null),
          api.get('/catalog/periods').catch(() => []),
        ]);
        setSchool(schoolData);
        const per = (periods || []).find((p) => p.id === periodId) || periods?.[0];
        setPeriod(per);

        // 3. Charger toutes les évaluations de la classe pour ce trimestre
        if (!clsId) return;
        const evals = await api.get(`/evaluations?classId=${clsId}${periodId ? `&periodId=${periodId}` : ''}`).catch(() => []);

        // 4. Regrouper par matière
        const subjectMap = {};
        (evals || []).forEach((ev) => {
          const subId = ev.subjectId;
          if (!subjectMap[subId]) {
            subjectMap[subId] = {
              subjectId: subId,
              subjectName: ev.subject?.name || '—',
              coefficient: ev.coefficient || 1,
              evaluations: [],
              controle: null,
              composition: null,
            };
          }
          // Trouver la note de cet élève
          const result = (ev.results || []).find((r) => r.studentId === studentId);
          const note = result?.totalObtained ?? null;
          const absent = result?.absent ?? false;

          subjectMap[subId].evaluations.push({ type: ev.type, note, bareme: ev.baremeTotal, absent });

          if (['CONTROLE', 'DEVOIR', 'DICTEE', 'EXERCICE'].includes(ev.type)) {
            subjectMap[subId].controle = { note, bareme: ev.baremeTotal, absent };
          }
          if (ev.type === 'COMPOSITION') {
            subjectMap[subId].composition = { note, bareme: ev.baremeTotal, absent };
          }
        });

        // 5. Calculer la moyenne par matière (sur /20)
        const subjectList = Object.values(subjectMap).map((s) => {
          const notes = [];
          if (s.controle?.note != null && !s.controle.absent) notes.push({ note: s.controle.note / s.controle.bareme * 20, weight: 1 });
          if (s.composition?.note != null && !s.composition.absent) notes.push({ note: s.composition.note / s.composition.bareme * 20, weight: 2 });
          const moyenne = notes.length > 0
            ? Math.round((notes.reduce((sum, n) => sum + n.note * n.weight, 0) / notes.reduce((sum, n) => sum + n.weight, 0)) * 100) / 100
            : null;
          return { ...s, moyenne };
        });

        setSubjects(subjectList);

        // 6. Calculer la moyenne générale pondérée
        const notedSubs = subjectList.filter((s) => s.moyenne != null);
        const totalCoeff = notedSubs.reduce((sum, s) => sum + s.coefficient, 0);
        const moyGen = totalCoeff > 0
          ? Math.round(notedSubs.reduce((sum, s) => sum + s.moyenne * s.coefficient, 0) / totalCoeff * 100) / 100
          : null;

        // 7. Calculer le rang dans la classe
        if (clsId && moyGen != null) {
          const allStudents = await api.get(`/students?classId=${clsId}`).catch(() => []);
          const rangs = [];
          for (const other of (allStudents || [])) {
            const otherEvals = await api.get(`/evaluations?classId=${clsId}${periodId ? `&periodId=${periodId}` : ''}`).catch(() => evals);
            const otherNotes = [];
            (otherEvals || []).forEach((ev) => {
              const r = (ev.results || []).find((res) => res.studentId === other.id);
              if (r?.totalObtained != null && !r.absent) {
                otherNotes.push({ note: r.totalObtained / ev.baremeTotal * 20, coeff: ev.coefficient || 1 });
              }
            });
            const tc = otherNotes.reduce((s, n) => s + n.coeff, 0);
            const moy = tc > 0 ? otherNotes.reduce((s, n) => s + n.note * n.coeff, 0) / tc : null;
            if (moy != null) rangs.push(moy);
          }
          rangs.sort((a, b) => b - a);
          const rang = rangs.findIndex((m) => m <= moyGen) + 1 || null;
          const moyClasse = rangs.length > 0 ? Math.round(rangs.reduce((s, v) => s + v, 0) / rangs.length * 100) / 100 : null;
          setClassStats({ moyenneGenerale: moyGen, rang: rang || null, moyenneClasse, effectif: rangs.length });
        } else {
          setClassStats({ moyenneGenerale: moyGen, rang: null, moyenneClasse: null, effectif: null });
        }

      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [studentId, periodId]);

  if (loading) return <div className="loading">Génération du bulletin…</div>;
  if (error)   return <div className="form-error">{error}</div>;
  if (!student) return <div className="empty">Élève introuvable.</div>;

  const { moyenneGenerale, rang, moyenneClasse, effectif } = classStats;
  const appreciation = moyenneGenerale != null ? APPRECIATION_NOTE(moyenneGenerale, 20) : '—';
  const annee = new Date().getFullYear();

  // Grouper matières par domaine
  const GROUPES = {
    'Français': ['Lecture / Écriture', 'Dictée', 'Grammaire', 'Conjugaison', 'Orthographe'],
    'Mathématiques': ['Calcul'],
    'Sciences & Société': ["Sciences d'éveil", 'Histoire-Géographie'],
    'Éducation': ['EPS', 'Éducation civique et morale', 'Dessin / Travaux manuels'],
  };

  function getGroupe(name) {
    for (const [g, mats] of Object.entries(GROUPES)) {
      if (mats.some((m) => name?.includes(m.split(' ')[0]))) return g;
    }
    return 'Autres matières';
  }

  const byGroupe = {};
  subjects.forEach((s) => {
    const g = getGroupe(s.subjectName);
    if (!byGroupe[g]) byGroupe[g] = [];
    byGroupe[g].push(s);
  });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <button className="btn-ghost btn-sm" onClick={() => navigate(-1)}>← Retour</button>
        <button className="btn-ghost btn-sm" onClick={() => window.print()}>🖨 Imprimer</button>
      </div>

      <div className="bulletin">
        {/* En-tête */}
        <div className="bulletin-head">
          <div className="bulletin-mark">S</div>
          <div className="bulletin-school">
            <strong>{school?.name || 'Groupe Scolaire La Providence'}</strong>
            <span>{school?.address || 'Éducation nationale'}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="bulletin-title">Bulletin Élémentaire</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Année {annee}-{annee + 1}</div>
          </div>
        </div>

        {/* Identité */}
        <div className="bulletin-identity">
          <div><span>Nom</span><b>{student?.lastName}</b></div>
          <div><span>Prénom</span><b>{student?.firstName}</b></div>
          <div><span>Matricule</span><b>{student?.matricule}</b></div>
          <div><span>Classe</span><b>{classInfo?.name || '—'}</b></div>
        </div>

        {/* Info barème */}
        <div style={{ fontSize: 11, color: '#1d4ed8', background: '#eff6ff', padding: '6px 12px', borderRadius: 6, marginBottom: 8 }}>
          📚 Contrôles notés sur /10 — Compositions notées sur /20 — Moyenne pondérée sur /20
        </div>

        {/* Tableau des matières */}
        {subjects.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)', border: '0.5px dashed var(--line)', borderRadius: 8, margin: '12px 0' }}>
            Aucune évaluation saisie pour ce trimestre.
          </div>
        ) : (
          Object.entries(byGroupe).map(([groupe, mats]) => (
            <div key={groupe}>
              <div style={{ background: '#1a3a5c', color: '#fff', padding: '5px 12px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', marginTop: 8 }}>
                {groupe}
              </div>
              <table className="bulletin-table">
                <thead>
                  <tr>
                    <th style={{ width: '35%' }}>Matière</th>
                    <th className="num">Contrôle /10</th>
                    <th className="num">Composition /20</th>
                    <th className="num">Moyenne /20</th>
                    <th className="num">Coeff.</th>
                    <th>Appréciation</th>
                  </tr>
                </thead>
                <tbody>
                  {mats.map((mat, i) => {
                    const appr = mat.moyenne != null ? APPRECIATION_NOTE(mat.moyenne, 20) : '—';
                    const color = mat.moyenne != null ? (mat.moyenne >= 10 ? 'var(--success)' : 'var(--danger)') : 'inherit';
                    return (
                      <tr key={i}>
                        <td>{mat.subjectName}</td>
                        <td className="num mono">
                          {mat.controle?.absent ? 'Abs' : mat.controle?.note != null ? mat.controle.note : '—'}
                        </td>
                        <td className="num mono">
                          {mat.composition?.absent ? 'Abs' : mat.composition?.note != null ? mat.composition.note : '—'}
                        </td>
                        <td className="num mono" style={{ fontWeight: 700, color }}>
                          {mat.moyenne ?? '—'}
                        </td>
                        <td className="num">{mat.coefficient}</td>
                        <td style={{ fontSize: 12 }}>{appr}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))
        )}

        {/* Synthèse */}
        <div className="bulletin-summary" style={{ marginTop: 16 }}>
          <div className="bulletin-average">
            <span>Moyenne générale — {period?.name || 'Trimestre'}</span>
            <strong>{moyenneGenerale ?? '—'}<em>/20</em></strong>
            <span style={{ marginTop: 6 }}>{moyenneGenerale != null ? appreciation : ''}</span>
          </div>
          <div className="bulletin-kpis">
            <div>
              <span>Rang dans la classe</span>
              <b>{rang != null ? `${rang} / ${effectif}` : '—'}</b>
            </div>
            <div>
              <span>Moyenne de classe</span>
              <b>{moyenneClasse != null ? `${moyenneClasse}/20` : '—'}</b>
            </div>
            <div>
              <span>Appréciation</span>
              <b style={{ fontSize: '0.85rem' }}>{moyenneGenerale != null ? appreciation : '—'}</b>
            </div>
          </div>
        </div>

        {/* Appréciation directeur */}
        <div style={{ marginTop: 14, padding: '12px 16px', border: '0.5px solid var(--line)', borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
            Appréciation du Directeur
          </div>
          <div style={{ minHeight: 36, fontSize: 13 }}>—</div>
        </div>

        {/* Absences */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 14 }}>
          {['Absences justifiées', 'Absences injustifiées', 'Retards'].map((label) => (
            <div key={label} style={{ textAlign: 'center', padding: 10, border: '0.5px solid var(--line)', borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontWeight: 700 }}>—</div>
            </div>
          ))}
        </div>

        {/* Signatures */}
        <div className="bulletin-foot" style={{ marginTop: 28 }}>
          {["L'enseignant(e)", 'Le Directeur', 'Les Parents / Tuteurs'].map((sig) => (
            <div key={sig} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 36 }}>{sig}</div>
              <div className="sign-line" style={{ margin: '0 auto' }} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
